// AI-assisted: Claude 2026-08-08, human-reviewed: ausstehend
// 33 Vorlagen in einer Liste sind ein Katalog, keine Empfehlung (Thors Fund
// Runde 16). Die Vorlagen kommen als Familie × Stufe — „Hyrox – Einsteiger",
// „Hyrox – Profi" —, das steckt im Kürzel (slug). Daraus bauen wir Kategorien,
// ohne dafür ein Feld in der Datenbank zu brauchen.
//
// ponytail: Familie/Stufe kommen aus dem slug. Neue Vorlagen ohne bekannte
// Familie landen sichtbar unter „Weitere" — nichts verschwindet still.
import type { Plan } from "./types"

export const STUFEN = ["einsteiger", "fortgeschritten", "profi"] as const
export type Stufe = (typeof STUFEN)[number] | ""

const STUFEN_NAME: Record<string, string> = {
  einsteiger: "Einsteiger",
  fortgeschritten: "Fortgeschritten",
  profi: "Profi",
}

export interface Kategorie {
  id: string
  name: string
  hinweis: string
  familien: readonly string[]
}

export const KATEGORIEN: readonly Kategorie[] = [
  {
    id: "kraft",
    name: "Kraft & Aufbau",
    hinweis: "Stärker werden, Muskeln aufbauen",
    familien: ["ganzkoerper", "push-pull", "kettlebell"],
  },
  {
    id: "kondition",
    name: "Kondition & Wettkampf",
    hinweis: "Puste, Tempo, Wettkampftage",
    familien: ["hyrox", "crossfit", "schlitten"],
  },
  {
    id: "cheer",
    name: "Cheerleading",
    hinweis: "Stunts, Sprünge, Körperspannung",
    familien: ["cheer-bases", "flyer-tanzmariechen"],
  },
  {
    id: "aufbau",
    name: "Aufbau nach Beschwerden",
    hinweis: "Rücken, Schulter, Knie — ruhig anfangen",
    familien: ["lws-stufen", "lws-einstieg", "impingement", "impingement-physio-studio",
      "schlitten-schulter-60", "knee-rehab"],
  },
]

/** Stufe aus dem Kürzel: „hyrox-einsteiger" → „einsteiger". */
export function stufeVon(plan: Plan): Stufe {
  const slug = plan.slug ?? ""
  for (const s of STUFEN) if (slug.endsWith(`-${s}`)) return s
  return ""
}

/** Familie aus dem Kürzel: „hyrox-einsteiger" → „hyrox". */
export function familieVon(plan: Plan): string {
  const slug = plan.slug ?? ""
  const stufe = stufeVon(plan)
  return stufe ? slug.slice(0, -(stufe.length + 1)) : slug
}

/** Kategorie-id oder "" (dann: „Weitere"). */
export function kategorieVon(plan: Plan): string {
  const familie = familieVon(plan)
  return KATEGORIEN.find((k) => k.familien.includes(familie))?.id ?? ""
}

/** Anzeigename ohne die Stufe: „Hyrox – Einsteiger" → „Hyrox". */
export function familienName(plan: Plan): string {
  return plan.name.split(" – ")[0]
}

export function stufenName(plan: Plan): string {
  return STUFEN_NAME[stufeVon(plan)] ?? ""
}

/** Vorlagen nach Kategorie, innerhalb nach Familie und Stufe sortiert.
 *  Unbekannte Familien kommen zum Schluss unter der id "". */
export function nachKategorie(plaene: readonly Plan[]): Array<[Kategorie | null, Plan[]]> {
  const reihenfolge = (p: Plan) => {
    const i = STUFEN.indexOf(stufeVon(p) as (typeof STUFEN)[number])
    return i === -1 ? 0 : i
  }
  const sortiere = (a: Plan, b: Plan) =>
    familienName(a).localeCompare(familienName(b), "de") || reihenfolge(a) - reihenfolge(b)

  const gruppen: Array<[Kategorie | null, Plan[]]> = []
  for (const k of KATEGORIEN) {
    const g = plaene.filter((p) => kategorieVon(p) === k.id).sort(sortiere)
    if (g.length) gruppen.push([k, g])
  }
  const rest = plaene.filter((p) => !kategorieVon(p)).sort(sortiere)
  if (rest.length) gruppen.push([null, rest])
  return gruppen
}

export interface Vorschlag {
  plan: Plan
  warum: string
}

// Drei bewusst verschiedene Einstiege statt einer 33er-Liste. Reihenfolge =
// Vorrang: das erste gefundene Kürzel jeder Zeile wird vorgeschlagen.
const START: Array<[string[], string]> = [
  [["ganzkoerper-einsteiger", "push-pull-einsteiger"], "Der Allrounder: ganzer Körper, zwei Tage die Woche"],
  [["hyrox-einsteiger", "crossfit-einsteiger"], "Wenn dir Puste und Tempo wichtiger sind als Gewichte"],
  [["lws-stufen-einsteiger", "lws-einstieg"], "Wenn der Rücken mitredet: ruhig aufbauen statt draufhauen"],
]

/** Drei Vorschläge für den Anfang — aus drei verschiedenen Richtungen. */
export function startVorschlaege(plaene: readonly Plan[]): Vorschlag[] {
  const vorlagen = plaene.filter((p) => !p.owner)
  const treffer: Vorschlag[] = []
  for (const [slugs, warum] of START) {
    const plan = slugs.map((s) => vorlagen.find((p) => p.slug === s)).find(Boolean)
    if (plan) treffer.push({ plan, warum })
  }
  return treffer
}
